<template>
  <button
    class="ui-tag-option"
    :class="[
      mode && `mode-${mode}`,
      `page-${serviceTypeValue}`,
      `size-${size}`,
    ]"
    @click="emits('click')"
  >
    <template
      v-if="mode"
    >
      <span v-if="mode === 'exclude'" class="ui-tag-option-label">
        {{ lss('除外', 'Exclude') }}
      </span>
      <span v-else class="ui-tag-option-label">
        {{ lss('含む', 'Include') }}
      </span>
    </template>
    <slot />
    <BaseIcon
      v-if="icon"
      :name="icon"
      :size="iconSize"
      class="icon"
    />
  </button>
</template>

<script lang="ts" setup>
import type { SVG_NAMES } from '~/components/icons'

interface Props {
  mode?: 'include' | 'exclude'
  serviceType?: 'hina' | 'jps'
  size?: 'medium' | 'small'
  icon?: SVG_NAMES
}

interface Emits {
  (e: 'click'): void
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
})
const emits = defineEmits<Emits>()

const { lss } = useI18n()

const iconSize = 'size-6'
const { serviceType } = useServiceType()
const serviceTypeValue = props.serviceType || serviceType
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.page-jps,
.page-hina {
  --include-background-color: #{$jps-gray-800};
  --include-border-color: #{$jps-gray-800};
  --include-text-color: #{$white};
  --include-label-text-color: var(--include-background-color);
  --include-label-background-color: #f3f3f3;
  --include-hover-text-color: #{$white-hover};
  --include-hover-background-color: #{$jps-gray-600};
  --include-hover-border-color: #{$jps-gray-600};
  --include-hover-label-text-color: var(--include-hover-background-color);

  --exclude-background-color: #{$jps-key-red};
  --exclude-border-color: #{$jps-key-red};
  --exclude-text-color: #{$white};
  --exclude-label-text-color: var(--exclude-hover-background-color);
  --exclude-label-background-color: #ffffff;
  --exclude-hover-text-color: #{$white-hover};
  --exclude-hover-background-color: #{$jps-key-red-hover};
  --exclude-hover-border-color: #{$jps-key-red-hover};
  --exclude-hover-label-text-color: var(--exclude-hover-background-color);
}

.ui-tag-option {
  @include tag-base;
  @include transition-colors;
  gap: 0.25rem;
  cursor: pointer;
  padding: 0.375rem 0.5rem;
  overflow-wrap: anywhere;
  white-space: wrap;

  &.size-small {
    min-height: 1.625rem;
    padding: 0.2rem 0.375rem;
  }

  &.size-medium {
    min-height: 2rem;
  }

  &.mode-include {
    border: 1px solid var(--include-border-color);
    @include tag-color(
      var(--include-background-color),
      var(--include-border-color),
      var(--include-text-color),
      var(--include-hover-text-color)
    );

    &:not(:disabled):hover {
      border-color: var(--include-hover-border-color);
      background-color: var(--include-hover-background-color);

      .ui-tag-option-label {
        color: var(--include-hover-label-text-color);
      }
    }
  }

  &.mode-exclude {
    border: 1px solid var(--exclude-border-color);
    @include tag-color(
      var(--exclude-background-color),
      var(--exclude-border-color),
      var(--exclude-text-color),
      var(--exclude-hover-text-color)
    );

    &:not(:disabled):hover {
      border-color: var(--exclude-hover-border-color);
      background-color: var(--exclude-hover-background-color);

      .ui-tag-option-label {
        color: var(--exclude-hover-label-text-color);
      }
    }
  }
}

.ui-tag-option-label {
  @include transition-all;
  font-size: $size-8;
  font-weight: bold;
  display: block;
  padding: 1px 4px;
  line-height: 1.2;
  border-radius: 2px;
  flex-shrink: 0;
  margin: {
    left: 0.25rem;
    right: 0.125rem;
  }
}

.icon {
  flex-shrink: 0;
}

.mode-include {
  .ui-tag-option-label {
    color: var(--include-background-color);
    background-color: var(--include-label-background-color);
  }
}

.mode-exclude {
  .ui-tag-option-label {
    color: var(--exclude-background-color);
    background-color: var(--exclude-label-background-color);
  }
}
</style>